import { getTabId } from './messageAlertCoordinator'

/**
 * Tells every Atlas tab when another one comes to the front, so a hidden tab can
 * tell "the reader moved to Atlas elsewhere" apart from "the reader looked away".
 * Without BroadcastChannel nothing is announced and nothing is heard: a single tab.
 */
const BROADCAST_CHANNEL_NAME = 'atlas-tab-visibility'

interface ITabVisibleMessage {
  type: 'visible'
  tabId: string
}

const isTabVisibleMessage = (data: unknown): data is ITabVisibleMessage => {
  if (!data || typeof data !== 'object') return false
  const candidate = data as Partial<ITabVisibleMessage>
  return candidate.type === 'visible' && typeof candidate.tabId === 'string'
}

const openChannel = (): BroadcastChannel | null =>
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(BROADCAST_CHANNEL_NAME) : null

/**
 * Every Atlas page announces itself, so a tab without AIM open still counts as Atlas.
 * A page that opens in the foreground gets no `visibilitychange`, so it announces on load.
 */
export const registerAtlasTabVisibility = (): void => {
  const channel = openChannel()
  if (!channel) return
  const announceIfVisible = () => {
    if (document.visibilityState !== 'visible') return
    const message: ITabVisibleMessage = { type: 'visible', tabId: getTabId() }
    channel.postMessage(message)
  }
  document.addEventListener('visibilitychange', announceIfVisible)
  announceIfVisible()
}

/** Calls `listener` whenever a different Atlas tab becomes visible. Returns the unsubscribe. */
export const onOtherAtlasTabVisible = (listener: () => void): (() => void) => {
  const channel = openChannel()
  if (!channel) return () => undefined
  channel.addEventListener('message', (event: MessageEvent) => {
    if (isTabVisibleMessage(event.data) && event.data.tabId !== getTabId()) listener()
  })
  return () => channel.close()
}
